import { parseProjectilePool } from './projectile-targeting';
import { parsePaintedWall, type PaintedWallGeometry } from './painted-wall-geometry';
import {parsePaintedWallSectionState,type PaintedWallSectionState} from './painted-wall-section-state';
import { parseFt3dZoneVisual, type Ft3dZoneVisual } from './ft3d-zone-visual';
export const FT3D_AOE_PLACEMENT_SCHEMA = 'ft.aoe-placement.intent.v1' as const;
export const FT3D_AOE_PLACEMENT_STAGE_SCHEMA = 'ft.aoe-placement.stage.v1' as const;
export const FT3D_AOE_REMOTE_PREVIEW_SCHEMA = 'ft.aoe-placement.remote-preview.v1' as const;

export type Ft3dAoeShape = 'sphere' | 'circle' | 'cylinder' | 'cube' | 'square' | 'line' | 'cone';

export interface Ft3dAoePoint {
  x: number;
  y: number;
  z: number;
}

export interface Ft3dAoePlacementIntent {
  thrownObjectSizeId?: string;
  /** 2026-10-07 telekinetic Throw Object: the world prop the throw LEAVES from (range, arc and line of effect run from it, not the
   *  caster). Absent = the caster. */
  launchEntityId?: string;
  wallAttachment?: 'surface' | 'floating';
  wallGeometry?: PaintedWallGeometry;
  intentId: number;
  encounterId: string;
  casterId: string;
  /** 2026-10-07 (held-target-move.ts — Psion Telekinetic Puppet Strings / World in Motion): when the placed body is NOT the acting creature
   *  (a caster moving a creature it holds), `casterId` is the PLACED creature (range, footprint, landing and sight are measured from it) and
   *  this is the acting creature — the dock that shows Confirm and the caster the shared preview is authorized for. Absent = casterId. */
  controllerId?: string;
  abilityId: string;
  abilityName: string;
  shape: Ft3dAoeShape;
  distanceFt: number;
  heightFt: number;
  depthFt: number;
  rangeFt: number | null;
  /** Reach uses the caster's persisted combat span, rather than the visual launch body's height. */
  reachRange?: boolean;
  selfRange: boolean;
  persistent: boolean;
  followsTarget: boolean;
  groundOnly: boolean;
  abilityDoesNotEffectInventory: boolean;
  /** `ability_only_affects_inventory` layer (2026-09-16, Siege Ram): the preview collects inventory only, never
   *  creatures — mirrors the server's area expansion. Optional so older envelopes stay valid. */
  abilityOnlyAffectsInventory?: boolean;
  /** `arcing_trajectory` layer (owner 2026-09-15): the area is lobbed along a ballistic parabola (shared sampler
   *  `aoe-arc-trajectory.ts`) instead of a straight C→CTR line — clears walls it can arc over, is stopped by a
   *  ceiling, and needs no sight of the landing point. Centered shapes only. Optional so older envelopes stay valid. */
  arcingTrajectory?: boolean;
  /** Hand throws (Throw Object, owner 2026-09-15) fly the FLATTEST arc that reaches — 45°, apex = distance / 4 —
   *  instead of the range-derived lob, so a mug tossed 10 ft indoors peaks 2.5 ft and clears the ceiling. The
   *  sampler gets a null range for it. Optional so older envelopes stay valid. */
  arcFlat?: boolean;
  /** AP this placement will spend, shown as ⚡ on the aim readout (the movement label's glyphs). Set only by a producer
   *  that knows a cost the catalog button cannot carry — Throw Object drawn from the bag costs 2 in combat, from the
   *  hand 1 (owner 2026-09-16). Presentation only; Janus debits. Optional so older envelopes stay valid. */
  actionPointCost?: number;
  /** Self-range damage/contest casts (and attack-ability areas) never target the caster (owner 2026-08-26,
   *  mirrors the XZ widget's AOETargetResolver rule). Optional so older envelopes stay valid. */
  excludesCaster?: boolean;
  upcastable: boolean;
  baseCastLevel: number;
  castLevel: number;
  maxCastLevel: number;
  upcastIncreaseFt: number;
  projectilePool?: import('./projectile-targeting').ProjectilePool & { assigned: number };
  projectileImpacts?: Ft3dAoePoint[];
  /** The placement renderer is shared by ordinary area effects and ground-targeted summons. */
  /** 'creature' = a GM dropping an encounter creature from the add-creature picker (owner 2026-09-11): the
   *  summon ghost with NO caster or ability — nothing is measured from a caster and nothing is cast. */
  placementKind?: 'aoe' | 'summon' | 'teleport' | 'creature' | 'zone-edit';
  /** 'zone-edit' (MV2 S1b, 2026-09-28): a GM moving an already-placed persistent zone. No caster and no ability are measured —
   *  no range, line of effect or sight — the shape starts at the zone's stored span and the confirm commits the new span
   *  through window.ftTryCommitZoneMove (never a cast). */
  editStatusId?: string;
  editSpan?: { origin: Ft3dAoePoint; endpoint: Ft3dAoePoint };
  /** Summon preview metadata. It is presentation-only; the cast wire still receives only the ground point. */
  summonCreatureId?: string;
  summonName?: string;
  summonPictureUrl?: string;
  summonWidthFt?: number;
  summonHeightFt?: number;
}

export interface Ft3dAoeTargetPreview {
  entityId: string;
  kind: 'creature' | 'inventory';
  name: string;
  pictureUrl: string;
}

export interface Ft3dAoePlacementEnvelope {
  schema: typeof FT3D_AOE_PLACEMENT_SCHEMA;
  version: number;
  /** Fresh arm resets scene placement; updates within the held draft preserve it. */
  resetPlacement?: boolean;
  intent: Ft3dAoePlacementIntent | null;
}

export interface Ft3dAoePlacementStageEnvelope {
  wallGeometry?: PaintedWallGeometry;
  propagation?: import('../../../ft3d/engine/ft3d-aoe-propagation.js').AoePropagation | null;
  schema: typeof FT3D_AOE_PLACEMENT_STAGE_SCHEMA;
  version: number;
  targetSelection: string;
  entityIds: string[];
  targets: Ft3dAoeTargetPreview[];
  origin: Ft3dAoePoint;
  endpoint: Ft3dAoePoint;
  center: Ft3dAoePoint;
  /** Hover previews are checked before a scene click commits the point. */
  placed?: boolean;
  inRange: boolean;
  lineOfEffect: 'clear' | 'blocked';
  castDistanceFt: number;
  /** Teleport only: the ft_teleport_destination() refusal the scene PROVED for this landing (distance, vertical
   *  space, clear space) with PHP's exact message; null = none proven (the commit still judges the rest). */
  landingFailure?: string | null;
  /** The aim-visibility refusal the scene PROVED for the final (best-fit) point, with PHP's exact message — set only
   *  when no point on the caster's line is visible; null = seen or not judgeable (the cast still judges it). */
  aimFailure?: string | null;
}

export interface Ft3dAoeRemotePreviewEnvelope {
  wallGeometry?: PaintedWallGeometry;
  targetIds: string[];
  schema: typeof FT3D_AOE_REMOTE_PREVIEW_SCHEMA;
  version: 1;
  encounterId: string;
  fromUserId: string;
  sequence: number;
  casterId: string;
  shape: Ft3dAoeShape;
  distanceFt: number;
  heightFt: number;
  depthFt: number;
  groundOnly: boolean;
  origin: Ft3dAoePoint;
  endpoint: Ft3dAoePoint;
  center: Ft3dAoePoint;
  rotationDeg: number;
  color: string;
}

function recordOf(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function finitePoint(value: unknown): Ft3dAoePoint | null {
  const row = recordOf(value);
  if (!row) return null;
  const x = Number(row.x);
  const y = Number(row.y);
  const z = Number(row.z);
  return Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z) ? { x, y, z } : null;
}

export function isFt3dAoeShape(value: unknown): value is Ft3dAoeShape {
  return ['sphere', 'circle', 'cylinder', 'cube', 'square', 'line', 'cone'].includes(String(value));
}

export function parseFt3dAoePlacementEnvelope(value: unknown): Ft3dAoePlacementEnvelope | null {
  const root = recordOf(value);
  if (root?.schema !== FT3D_AOE_PLACEMENT_SCHEMA || !Number.isInteger(root.version) || Number(root.version) < 0) {
    return null;
  }
  if (root.intent === null) {
    return { schema: FT3D_AOE_PLACEMENT_SCHEMA, version: Number(root.version), intent: null };
  }
  const intent = recordOf(root.intent);
  const rangeFt = intent?.rangeFt === null ? null : Number(intent?.rangeFt);
  const baseCastLevel = Number(intent?.baseCastLevel ?? 0);
  const castLevel = Number(intent?.castLevel ?? baseCastLevel);
  const maxCastLevel = Number(intent?.maxCastLevel ?? castLevel);
  const upcastIncreaseFt = Number(intent?.upcastIncreaseFt ?? 0);
  const placementKind = intent?.placementKind === 'teleport' ? 'teleport' : intent?.placementKind === 'summon' ? 'summon'
    : intent?.placementKind === 'creature' ? 'creature' : intent?.placementKind === 'zone-edit' ? 'zone-edit' : 'aoe';
  const editSpanRow = recordOf(intent?.editSpan);
  const editOrigin = finitePoint(editSpanRow?.origin), editEndpoint = finitePoint(editSpanRow?.endpoint);
  const editStatusId = String(intent?.editStatusId ?? '').trim().toUpperCase();
  // Summons and GM creature drops both preview a creature ghost; only a creature drop has no caster/ability.
  const creatureGhost = placementKind === 'summon' || placementKind === 'creature';
  const summonWidthFt = Number(intent?.summonWidthFt ?? 0);
  const summonHeightFt = Number(intent?.summonHeightFt ?? 0);
  if (!intent
    || !Number.isInteger(intent.intentId) || Number(intent.intentId) !== Number(root.version)
    || !String(intent.encounterId ?? '').trim()
    || (placementKind !== 'creature' && placementKind !== 'zone-edit' && (!String(intent.casterId ?? '').trim() || !String(intent.abilityId ?? '').trim()))
    || (placementKind === 'zone-edit' && (!/^[0-9A-F]{8}(-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(editStatusId) || !editOrigin || !editEndpoint))
    || !isFt3dAoeShape(intent.shape)
    || !Number.isFinite(Number(intent.distanceFt)) || Number(intent.distanceFt) <= 0
    || !Number.isFinite(Number(intent.heightFt)) || Number(intent.heightFt) <= 0
    || !Number.isFinite(Number(intent.depthFt)) || Number(intent.depthFt) <= 0
    || (rangeFt !== null && (!Number.isFinite(rangeFt) || rangeFt < 0))
    || ![baseCastLevel, castLevel, maxCastLevel, upcastIncreaseFt].every(Number.isFinite)
    || baseCastLevel < 0 || castLevel < baseCastLevel || maxCastLevel < castLevel
    || upcastIncreaseFt < 0
    || (creatureGhost && (
      !String(intent.summonCreatureId ?? '').trim()
      || !(summonWidthFt > 0) || !(summonHeightFt > 0)
    ))) return null;
  return {
    schema: FT3D_AOE_PLACEMENT_SCHEMA,
    version: Number(root.version),
    ...(root.resetPlacement === true ? { resetPlacement: true } : {}),
    intent: {
      intentId: Number(intent.intentId),
      encounterId: String(intent.encounterId).trim().toUpperCase(),
      casterId: String(intent.casterId ?? '').trim().toUpperCase(),
      ...(typeof intent.controllerId === 'string' && /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(intent.controllerId.trim())
        ? { controllerId: intent.controllerId.trim().toUpperCase() } : {}),
      abilityId: String(intent.abilityId ?? '').trim().toUpperCase(),
      abilityName: String(intent.abilityName ?? 'Ability'),
      shape: String(intent.shape) as Ft3dAoeShape,
      distanceFt: Number(intent.distanceFt),
      heightFt: Number(intent.heightFt),
      depthFt: Number(intent.depthFt),
      rangeFt,
      ...(intent.reachRange === true ? { reachRange: true } : {}),
      selfRange: intent.selfRange === true,
      persistent: intent.persistent === true,
      followsTarget: intent.followsTarget === true,
      groundOnly: intent.groundOnly === true,
      ...(intent.wallAttachment === 'surface' || intent.wallAttachment === 'floating' ? { wallAttachment: intent.wallAttachment } : {}),
      abilityDoesNotEffectInventory: intent.abilityDoesNotEffectInventory === true,
      abilityOnlyAffectsInventory: intent.abilityOnlyAffectsInventory === true,
      excludesCaster: intent.excludesCaster === true,
      arcingTrajectory: intent.arcingTrajectory === true,
      arcFlat: intent.arcFlat === true,
      ...(typeof intent.thrownObjectSizeId === 'string' ? { thrownObjectSizeId: intent.thrownObjectSizeId } : {}),
      ...(typeof intent.launchEntityId === 'string' && /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(intent.launchEntityId.trim())
        ? { launchEntityId: intent.launchEntityId.trim().toUpperCase() } : {}),
      ...(Number(intent.actionPointCost) > 0 ? { actionPointCost: Math.min(99, Math.trunc(Number(intent.actionPointCost))) } : {}),
      upcastable: intent.upcastable === true,
      baseCastLevel,
      castLevel,
      maxCastLevel,
      upcastIncreaseFt,
      projectileImpacts: Array.isArray(intent.projectileImpacts)
        ? intent.projectileImpacts.slice(0, 1000).map(finitePoint).filter((point): point is Ft3dAoePoint => point !== null) : undefined,
      ...(parseProjectilePool(intent.projectilePool) ? { projectilePool: {
        ...parseProjectilePool(intent.projectilePool)!,
        assigned: Math.max(0, Math.min(Number(recordOf(intent.projectilePool)?.remaining), Math.trunc(Number(recordOf(intent.projectilePool)?.assigned) || 0))),
      } } : {}),
      placementKind,
      ...(placementKind === 'zone-edit' && editOrigin && editEndpoint ? { editStatusId, editSpan: { origin: editOrigin, endpoint: editEndpoint } } : {}),
      summonCreatureId: creatureGhost
        ? String(intent.summonCreatureId).trim().toUpperCase() : undefined,
      summonName: creatureGhost ? String(intent.summonName ?? 'Summon').trim() || 'Summon' : undefined,
      summonPictureUrl: creatureGhost ? String(intent.summonPictureUrl ?? '').trim() : undefined,
      summonWidthFt: creatureGhost ? summonWidthFt : undefined,
      summonHeightFt: creatureGhost ? summonHeightFt : undefined,
    },
  };
}

export function parseFt3dAoePlacementStageEnvelope(value: unknown): Ft3dAoePlacementStageEnvelope | null {
  const root = recordOf(value);
  const origin = finitePoint(root?.origin);
  const endpoint = finitePoint(root?.endpoint);
  const center = finitePoint(root?.center);
  const castDistanceFt = Number(root?.castDistanceFt ?? 0);
  const targets = Array.isArray(root?.targets)
    ? root.targets.flatMap(value => {
      const target = recordOf(value);
      const entityId = String(target?.entityId ?? '').trim().toUpperCase();
      const kind = String(target?.kind ?? '');
      if (!entityId || (kind !== 'creature' && kind !== 'inventory')) return [];
      return [{
        entityId,
        kind: kind as Ft3dAoeTargetPreview['kind'],
        name: String(target?.name ?? 'Target').trim() || 'Target',
        pictureUrl: String(target?.pictureUrl ?? '').trim(),
      }];
    })
    : [];
  if (root?.schema !== FT3D_AOE_PLACEMENT_STAGE_SCHEMA
    || !Number.isInteger(root.version) || Number(root.version) < 1
    || typeof root.targetSelection !== 'string' || !root.targetSelection.includes('|')
    || !Array.isArray(root.entityIds) || !origin || !endpoint || !center
    || !Number.isFinite(castDistanceFt) || castDistanceFt < 0
    || !['clear', 'blocked'].includes(String(root.lineOfEffect ?? 'clear'))) return null;
  return {
    schema: FT3D_AOE_PLACEMENT_STAGE_SCHEMA,
    version: Number(root.version),
    targetSelection: root.targetSelection,
    ...(parsePaintedWall(root.wallGeometry) ? { wallGeometry: parsePaintedWall(root.wallGeometry)! } : {}),
    propagation: recordOf(root.propagation)?.version === 1 ? root.propagation as unknown as import('../../../ft3d/engine/ft3d-aoe-propagation.js').AoePropagation : null,
    entityIds: [...new Set(root.entityIds.map(String).map(id => id.trim().toUpperCase()).filter(Boolean))],
    targets: [...new Map(targets.map(target => [target.entityId, target])).values()],
    origin,
    endpoint,
    center,
    placed: root.placed !== false,
    inRange: root.inRange === true,
    lineOfEffect: root.lineOfEffect === 'blocked' ? 'blocked' : 'clear',
    castDistanceFt,
    landingFailure: typeof root.landingFailure === 'string' && root.landingFailure.trim() !== '' ? root.landingFailure : null,
    aimFailure: typeof root.aimFailure === 'string' && root.aimFailure.trim() !== '' ? root.aimFailure : null,
  };
}

export function parseFt3dAoeRemotePreviewEnvelope(value: unknown): Ft3dAoeRemotePreviewEnvelope | null {
  const root = recordOf(value);
  const origin = finitePoint(root?.origin);
  const endpoint = finitePoint(root?.endpoint);
  const center = finitePoint(root?.center);
  const sequence = Number(root?.sequence);
  const distanceFt = Number(root?.distance_ft);
  const heightFt = Number(root?.height_ft);
  const depthFt = Number(root?.depth_ft);
  const rotationDeg = Number(root?.rotation_deg ?? 0);
  const encounterId = String(root?.encounter_id ?? '').trim().toUpperCase();
  const fromUserId = String(root?.from_user_id ?? '').trim().toUpperCase();
  const casterId = String(root?.caster_id ?? '').trim().toUpperCase();
  const color = String(root?.color ?? '').trim();
  const wallGeometry = parsePaintedWall(root?.wall_geometry);
  if (root?.wall_geometry != null && !wallGeometry) return null;
  if (root?.schema !== FT3D_AOE_REMOTE_PREVIEW_SCHEMA || Number(root.version) !== 1
    || !Number.isSafeInteger(sequence) || sequence < 1
    || !encounterId || !fromUserId || !casterId || !isFt3dAoeShape(root.shape)
    || !origin || !endpoint || !center
    || ![distanceFt, heightFt, depthFt].every(value => Number.isFinite(value) && value > 0)
    || !Number.isFinite(rotationDeg)
    || !/^#[0-9a-f]{6}$/i.test(color)) return null;
  return {
    schema: FT3D_AOE_REMOTE_PREVIEW_SCHEMA,
    ...(wallGeometry ? { wallGeometry } : {}),
    version: 1,
    encounterId,
    fromUserId,
    sequence,
    casterId,
    shape: String(root.shape) as Ft3dAoeShape,
    targetIds: Array.isArray(root.target_ids) ? [...new Set(root.target_ids.slice(0, 256)
      .filter((id): id is string => typeof id === 'string' && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id))
      .map(id => id.toUpperCase()))] : [],
    distanceFt,
    heightFt,
    depthFt,
    groundOnly: root.ground_only === true || root.ground_only === 1,
    origin,
    endpoint,
    center,
    rotationDeg,
    color,
  };
}

export const FT3D_PERSISTENT_ZONES_SCHEMA = 'ft.ft3d.persistent-zones.v1' as const;

/**
 * One PERSISTED zone anchor (a `status_aoe_source=1` persistent-AOE status row, or an
 * `aoe_follows_target` aura riding its carrier), projected by the parent page from the encounter
 * blob for the FT3D iframe to draw. Feet; `y` is floor-plane depth, `z` elevation — the same axes as
 * the placement stage envelope, so the iframe seats it with the placement renderer verbatim.
 *
 * Owner report 2026-09-09: the placement preview vanished on cast and nothing drew the zone the cast
 * left behind ("pAOE effects do not show outline of aoe"). The legacy XZ widget had
 * XZPersistentAOELayer for this; the FT3D runtime had no consumer at all.
 */
export interface Ft3dPersistentZoneProjection {
  abilityId?: string;
  wallGeometry?: PaintedWallGeometry;
  wallSections?: PaintedWallSectionState;
  visual?: Ft3dZoneVisual;
  statusId: string;
  connectedId: string;
  casterId: string;
  label: string;
  shape: Ft3dAoeShape;
  distanceFt: number;
  heightFt: number;
  depthFt: number;
  groundOnly: boolean;
  followsTarget: boolean;
  /** The creature/inventory the zone rides (follows-target) — the iframe re-seats it on that placement. */
  attachedEntityId: string | null;
  attachedKind: 'creature' | 'inventory' | null;
  origin: Ft3dAoePoint;
  endpoint: Ft3dAoePoint;
  center: Ft3dAoePoint;
  rotationDeg: number;
}

export interface Ft3dPersistentZonesEnvelope {
  schema: typeof FT3D_PERSISTENT_ZONES_SCHEMA;
  encounterId: string;
  zones: Ft3dPersistentZoneProjection[];
}

export function parseFt3dPersistentZoneRows(value: unknown): Ft3dPersistentZoneProjection[] {
  if (!Array.isArray(value)) return [];
  const out = new Map<string, Ft3dPersistentZoneProjection>();
  for (const candidate of value) {
    const row = recordOf(candidate);
    const statusId = String(row?.statusId ?? '').trim().toUpperCase();
    const origin = finitePoint(row?.origin);
    const endpoint = finitePoint(row?.endpoint);
    const center = finitePoint(row?.center);
    const distanceFt = Number(row?.distanceFt);
    const heightFt = Number(row?.heightFt);
    const depthFt = Number(row?.depthFt);
    const rotationDeg = Number(row?.rotationDeg ?? 0);
    const attachedKind = row?.attachedKind === 'creature' || row?.attachedKind === 'inventory' ? row.attachedKind : null;
    const attachedEntityId = String(row?.attachedEntityId ?? '').trim().toUpperCase() || null;
    const wallGeometry = parsePaintedWall(row?.wallGeometry);
    if (row?.wallGeometry != null && !wallGeometry) continue;
    const wallSections=wallGeometry&&row?.wallSections!=null?parsePaintedWallSectionState(wallGeometry,row.wallSections):null;
    if(row?.wallSections!=null&&!wallSections)continue;
    if (!statusId || !row || !isFt3dAoeShape(row.shape) || !origin || !endpoint || !center
      || ![distanceFt, heightFt, depthFt].every(value => Number.isFinite(value) && value > 0)
      || !Number.isFinite(rotationDeg)) continue;
    out.set(statusId, {
      abilityId: String(row?.abilityId ?? '').trim().toUpperCase(),
      ...(wallGeometry ? { wallGeometry } : {}),
      ...(wallSections ? {wallSections} : {}),
      statusId,
      connectedId: String(row.connectedId ?? '').trim().toUpperCase(),
      ...(parseFt3dZoneVisual(row.visual,row.shape)?{visual:parseFt3dZoneVisual(row.visual,row.shape)}:{}),
      casterId: String(row.casterId ?? '').trim().toUpperCase(),
      label: String(row.label ?? '').trim() || 'Zone',
      shape: String(row.shape) as Ft3dAoeShape,
      distanceFt,
      heightFt,
      depthFt,
      groundOnly: row.groundOnly === true,
      followsTarget: row.followsTarget === true,
      attachedEntityId: attachedKind ? attachedEntityId : null,
      attachedKind: attachedEntityId ? attachedKind : null,
      origin,
      endpoint,
      center,
      rotationDeg,
    });
  }
  return [...out.values()];
}

/** Depth-bearing FT3D wire: point x:y:z, directional/center span ox:oy:oz:ex:ey:ez. */
export function buildFt3dAoeTargetSelection(
  intent: Ft3dAoePlacementIntent,
  origin: Ft3dAoePoint,
  endpoint: Ft3dAoePoint,
  center: Ft3dAoePoint,
  entityIds: readonly string[],
): string {
  if (intent.placementKind === 'summon' || intent.placementKind === 'teleport' || intent.placementKind === 'creature') {
    const coords = [center.x, center.y, center.z].map(value => value.toFixed(3)).join(':');
    return `${intent.encounterId}|${coords}`;
  }
  const centered = ['sphere', 'circle', 'cylinder', 'cube', 'square'].includes(intent.shape);
  const boxy = intent.shape === 'cube' || intent.shape === 'square';
  const axisGiven = boxy && (Math.abs(endpoint.x - origin.x) > 1e-6 || Math.abs(endpoint.y - origin.y) > 1e-6);
  let coords: string;
  if (axisGiven) {
    // A ROTATED square/cube (owner 2026-08-26): origin→endpoint is the box's local x-axis through the
    // centre (its midpoint, so get_target_point_x()'s midpoint rule still lands on the centre); the
    // server's cube/square branch reads the axis direction as the rotation.
    coords = [origin.x, origin.y, origin.z, endpoint.x, endpoint.y, endpoint.z]
      .map(value => value.toFixed(3)).join(':');
  } else if (intent.persistent && centered) {
    const half = intent.shape === 'cube' || intent.shape === 'square'
      ? intent.distanceFt / 2
      : intent.distanceFt;
    coords = [center.x - half, center.y, center.z, center.x + half, center.y, center.z]
      .map(value => value.toFixed(3)).join(':');
  } else if (intent.shape === 'line' || intent.shape === 'cone') {
    coords = [origin.x, origin.y, origin.z, endpoint.x, endpoint.y, endpoint.z]
      .map(value => value.toFixed(3)).join(':');
  } else {
    coords = [center.x, center.y, center.z].map(value => value.toFixed(3)).join(':');
  }
  const ids = [...new Set(entityIds.map(String).map(id => id.trim().toUpperCase()).filter(Boolean))];
  return `${intent.encounterId}|${coords}${ids.length ? `,${ids.join(',')}` : ''}`;
}
